'use client'

import React from 'react'
import { cx } from 'class-variance-authority'
import { CaretLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretLeft'
import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { CircleNotchIcon } from '@phosphor-icons/react/dist/ssr/CircleNotch'
import { FilePlusIcon } from '@phosphor-icons/react/dist/ssr/FilePlus'
import { PlusCircleIcon } from '@phosphor-icons/react/dist/ssr/PlusCircle'
import { ArrowsClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowsClockwise'
import { Star } from '@phosphor-icons/react/dist/ssr/Star'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { XCircleIcon } from '@phosphor-icons/react/dist/ssr/XCircle'

import type {
  DynamicImageCollectionItem,
  DynamicImageCollectionValue,
  DynamicImageKind,
  DynamicImageReference,
  DynamicImageUploadIntent,
  DynamicImageValue,
  Field,
} from '@skop-app/lib-domain/types'
import { formatFileSize } from '@skop-app/lib-domain/utils'
import { Button } from '@/components/atoms/Button/button.atom'
import { ScrollArea } from '@/components/atoms/ScrollArea/scrollArea.atom'

type ImageMetadata = {
  width?: number
  height?: number
}

type ImagePreset = {
  accept: string
  aspectRatio: number
  maxSizeBytes: number
  processing: {
    enabled?: boolean
    maxWidth: number
    maxHeight: number
    quality: number
    output: 'preserve' | 'jpeg' | 'png' | 'webp'
  }
  emptyTitle: string
  emptyHint: string
  objectFit: 'cover' | 'contain'
  previewClassName: string
}

type ResolvedImageTexts = {
  emptyTitle: string
  emptyHint: string
  chooseButtonLabel: string
  replaceButtonLabel: string
  removeButtonLabel: string
  addButtonLabel: string
  addMoreButtonLabel: string
  primaryLabel: string
  addAriaLabel?: string
  replaceAriaLabel?: string
  removeAriaLabel?: string
  invalidImageMessage: string
  unsupportedFormatMessage?: string
  maxSizeMessage?: string
  maxCountMessage?: string
  minWidthMessage?: string
  minHeightMessage?: string
  readDimensionsErrorMessage: string
  readImageErrorMessage: string
  prepareErrorMessage: string
}

type ImageReadErrorTexts = Pick<
  ResolvedImageTexts,
  'readDimensionsErrorMessage' | 'readImageErrorMessage'
>

type DynamicImageUploadFieldProps = {
  id: string
  name: string
  field: Field
  value: unknown
  error?: unknown
  disabled?: boolean
  ariaDescribedBy?: string
  ariaInvalid?: boolean
  onChange: (value: DynamicImageValue | DynamicImageCollectionValue) => void
  onBlur: () => void
}

type PreparedUpload = {
  upload: DynamicImageUploadIntent
}

const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024

const SINGLE_PREVIEW_MAX_HEIGHT_BY_KIND: Record<DynamicImageKind, number> = {
  profile: 224,
  logo: 288,
  cover: 352,
}

const IMAGE_PRESETS: Record<DynamicImageKind, ImagePreset> = {
  profile: {
    accept: 'image/png,image/jpeg,image/webp',
    aspectRatio: 1,
    maxSizeBytes: DEFAULT_MAX_SIZE_BYTES,
    processing: {
      maxWidth: 720,
      maxHeight: 720,
      quality: 0.85,
      output: 'jpeg',
    },
    emptyTitle: 'Ajouter une photo',
    emptyHint: 'PNG, JPG ou WebP. Le cadrage carré est privilégié.',
    objectFit: 'cover',
    previewClassName: 'mx-auto max-w-44 rounded-full',
  },
  logo: {
    accept: 'image/png,image/jpeg,image/webp,image/svg+xml',
    aspectRatio: 12 / 7,
    maxSizeBytes: DEFAULT_MAX_SIZE_BYTES,
    processing: {
      maxWidth: 1400,
      maxHeight: 1400,
      quality: 0.9,
      output: 'preserve',
    },
    emptyTitle: 'Ajouter un logo',
    emptyHint: 'PNG, SVG, JPG ou WebP. La transparence est conservée.',
    objectFit: 'contain',
    previewClassName: 'w-full rounded-md',
  },
  cover: {
    accept: 'image/png,image/jpeg,image/webp',
    aspectRatio: 16 / 7,
    maxSizeBytes: 8 * 1024 * 1024,
    processing: {
      maxWidth: 2200,
      maxHeight: 1200,
      quality: 0.86,
      output: 'jpeg',
    },
    emptyTitle: 'Ajouter une couverture',
    emptyHint: 'Image horizontale recommandée, 8 Mo maximum.',
    objectFit: 'cover',
    previewClassName: 'w-full rounded-md',
  },
}

const getImageTexts = (
  preset: ImagePreset,
  field: Field,
): ResolvedImageTexts => {
  const texts = field.imageTexts

  return {
    emptyTitle: texts?.emptyTitle ?? preset.emptyTitle,
    emptyHint: texts?.emptyHint ?? preset.emptyHint,
    chooseButtonLabel: texts?.chooseButtonLabel ?? 'Choisir une image',
    replaceButtonLabel: texts?.replaceButtonLabel ?? 'Remplacer',
    removeButtonLabel: texts?.removeButtonLabel ?? 'Supprimer',
    addButtonLabel: texts?.addButtonLabel ?? 'Ajouter',
    addMoreButtonLabel: texts?.addMoreButtonLabel ?? 'Ajouter une image',
    primaryLabel: texts?.primaryLabel ?? 'Image principale',
    addAriaLabel: texts?.addAriaLabel,
    replaceAriaLabel: texts?.replaceAriaLabel,
    removeAriaLabel: texts?.removeAriaLabel,
    invalidImageMessage:
      texts?.invalidImageMessage ?? 'Le fichier choisi doit être une image.',
    unsupportedFormatMessage: texts?.unsupportedFormatMessage,
    maxSizeMessage: texts?.maxSizeMessage,
    maxCountMessage: texts?.maxCountMessage,
    minWidthMessage: texts?.minWidthMessage,
    minHeightMessage: texts?.minHeightMessage,
    readDimensionsErrorMessage:
      texts?.readDimensionsErrorMessage ??
      "Impossible de lire les dimensions de l'image.",
    readImageErrorMessage:
      texts?.readImageErrorMessage ?? 'Impossible de lire cette image.',
    prepareErrorMessage:
      texts?.prepareErrorMessage ?? 'Impossible de préparer cette image.',
  }
}

const OUTPUT_MIME_TYPES = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
} as const

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
}

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
}

const EXTENSIONS_BY_MIME: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
  'image/webp': ['.webp'],
}

const isBrowserFile = (value: unknown): value is File =>
  typeof File !== 'undefined' && value instanceof File

const isObjectRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object'

const getValueStatus = (value: unknown) =>
  isObjectRecord(value) && typeof value.status === 'string'
    ? value.status
    : undefined

const getCurrentReference = (
  value: unknown,
): DynamicImageReference | undefined => {
  if (!value || isBrowserFile(value)) return undefined

  if (typeof value === 'string') {
    return { url: value }
  }

  if (!isObjectRecord(value)) return undefined

  if (value.status === 'existing' && isObjectRecord(value.ref)) {
    return value.ref
  }

  if (
    !value.status &&
    (typeof value.url === 'string' || typeof value.key === 'string')
  ) {
    return value as DynamicImageReference
  }

  return undefined
}

const getPreviousReference = (
  value: unknown,
): DynamicImageReference | undefined => {
  if (!isObjectRecord(value)) return getCurrentReference(value)

  if (value.status === 'pending' && isObjectRecord(value.upload?.previous)) {
    return value.upload.previous
  }

  if (value.status === 'removed' && isObjectRecord(value.previous)) {
    return value.previous
  }

  return getCurrentReference(value)
}

const getPendingUpload = (
  value: unknown,
): DynamicImageUploadIntent | undefined => {
  if (!isObjectRecord(value)) return undefined
  if (value.status !== 'pending' || !isObjectRecord(value.upload))
    return undefined
  if (!isBrowserFile(value.upload.file)) return undefined
  return value.upload as DynamicImageUploadIntent
}

const isCollectionItem = (
  value: unknown,
): value is DynamicImageCollectionItem => {
  if (!isObjectRecord(value) || typeof value.id !== 'string') return false

  if (value.status === 'existing') {
    return isObjectRecord(value.ref)
  }

  if (value.status === 'pending') {
    return isObjectRecord(value.upload) && isBrowserFile(value.upload.file)
  }

  return false
}

const sortCollectionItemsByOrder = (items: DynamicImageCollectionItem[]) =>
  [...items].sort((first, second) => first.order - second.order)

const getCollectionValue = (value: unknown): DynamicImageCollectionValue => {
  if (!isObjectRecord(value) || !Array.isArray(value.items)) {
    return { items: [], removed: [] }
  }

  return {
    items: sortCollectionItemsByOrder(
      value.items.filter(isCollectionItem).map((item, index) => ({
        ...item,
        order: typeof item.order === 'number' ? item.order : index,
      })),
    ),
    removed: Array.isArray(value.removed)
      ? value.removed.filter(isObjectRecord)
      : [],
  } as DynamicImageCollectionValue
}

const getCollectionItemReference = (
  item: DynamicImageCollectionItem,
): DynamicImageReference | undefined =>
  item.status === 'existing' ? item.ref : item.upload.previous

const getCollectionItemFileLabel = (item: DynamicImageCollectionItem) => {
  if (item.status === 'pending') {
    return `${item.upload.fileName} - ${formatFileSize(item.upload.size)}`
  }

  if (item.ref.width && item.ref.height) {
    return `${item.ref.width} x ${item.ref.height}px`
  }

  if (item.ref.key) return item.ref.key
  return undefined
}

const reorderCollectionItems = (
  items: DynamicImageCollectionItem[],
  primarySelectable: boolean,
) => {
  const hasPrimary = items.some((item) => item.isPrimary)

  return items.map((item, index) => ({
    ...item,
    order: index,
    isPrimary: primarySelectable
      ? hasPrimary
        ? item.isPrimary
        : index === 0
      : undefined,
  }))
}

const getSafeLabel = (field: Field) =>
  typeof field.label === 'string' && field.label.trim()
    ? field.label.trim()
    : field.name

const getFileNameExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  return extension && extension !== fileName.toLowerCase()
    ? extension
    : undefined
}

const getMimeTypeFromFileName = (fileName: string) => {
  const extension = getFileNameExtension(fileName)
  return extension ? MIME_BY_EXTENSION[extension] : undefined
}

const getFileContentType = (file: File) =>
  file.type || getMimeTypeFromFileName(file.name) || 'image/jpeg'

const getExtension = (file: File) => {
  const fromMime = EXTENSION_BY_MIME[file.type.toLowerCase()]
  if (fromMime) return fromMime

  return getFileNameExtension(file.name) ?? 'jpg'
}

const getOutputMimeType = (
  file: File,
  output: ImagePreset['processing']['output'],
) => {
  if (output === 'preserve') {
    return getFileContentType(file)
  }

  return OUTPUT_MIME_TYPES[output]
}

const replaceFileExtension = (fileName: string, extension: string) => {
  const baseName = fileName.replace(/\.[^.]+$/, '') || 'image'
  return `${baseName}.${extension}`
}

const sanitizePathPart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

const getRandomId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const getSuggestedKey = (field: Field, file: File) => {
  const prefix = (field.imageUploadPath || field.name)
    .split('/')
    .map(sanitizePathPart)
    .filter(Boolean)
    .join('/')
  const safePrefix = prefix || 'images'

  return `${safePrefix}/${getRandomId()}.${getExtension(file)}`
}

const parseAcceptRules = (accept: string) =>
  accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean)

const isAcceptedFile = (file: File, accept: string) => {
  const rules = parseAcceptRules(accept)
  if (rules.length === 0) return true

  const fileType = getFileContentType(file).toLowerCase()
  const fileNameExtension = getFileNameExtension(file.name)
  const fileExtension = fileNameExtension ? `.${fileNameExtension}` : undefined

  return rules.some((rule) => {
    if (rule.endsWith('/*')) {
      return fileType.startsWith(rule.replace('*', ''))
    }

    if (rule.startsWith('.')) {
      return fileExtension === rule
    }

    return (
      fileType === rule ||
      Boolean(
        fileExtension && EXTENSIONS_BY_MIME[rule]?.includes(fileExtension),
      )
    )
  })
}

const isImageFile = (file: File) =>
  file.type.startsWith('image/') ||
  Boolean(getMimeTypeFromFileName(file.name)?.startsWith('image/'))

const canUseObjectUrl = () =>
  typeof URL !== 'undefined' &&
  typeof URL.createObjectURL === 'function' &&
  typeof URL.revokeObjectURL === 'function'

const revokeObjectUrl = (url: string) => {
  if (!canUseObjectUrl()) return
  URL.revokeObjectURL(url)
}

const readImageMetadata = (
  file: File,
  texts: ImageReadErrorTexts,
): Promise<ImageMetadata> => {
  if (getFileContentType(file) === 'image/svg+xml') return Promise.resolve({})
  if (typeof Image === 'undefined' || !canUseObjectUrl()) {
    return Promise.resolve({})
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    const timer = window.setTimeout(() => {
      revokeObjectUrl(url)
      reject(new Error(texts.readDimensionsErrorMessage))
    }, 4000)

    image.onload = () => {
      window.clearTimeout(timer)
      const width = image.naturalWidth || image.width || undefined
      const height = image.naturalHeight || image.height || undefined
      revokeObjectUrl(url)
      resolve({ width, height })
    }

    image.onerror = () => {
      window.clearTimeout(timer)
      revokeObjectUrl(url)
      reject(new Error(texts.readImageErrorMessage))
    }

    image.src = url
  })
}

const resizeImageFile = async ({
  file,
  width,
  height,
  maxWidth,
  maxHeight,
  mimeType,
  quality,
}: {
  file: File
  width?: number
  height?: number
  maxWidth: number
  maxHeight: number
  mimeType: string
  quality: number
}): Promise<{ file: File; width?: number; height?: number }> => {
  const originalContentType = getFileContentType(file)

  if (!width || !height || originalContentType === 'image/svg+xml') {
    return { file, width, height }
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height, 1)
  if (ratio >= 1 && mimeType === originalContentType) {
    return { file, width, height }
  }

  if (
    typeof document === 'undefined' ||
    typeof Image === 'undefined' ||
    !canUseObjectUrl()
  ) {
    return { file, width, height }
  }

  const nextWidth = Math.round(width * ratio)
  const nextHeight = Math.round(height * ratio)
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context || typeof canvas.toBlob !== 'function') {
    return { file, width, height }
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()

    const finishWithOriginal = () => {
      revokeObjectUrl(url)
      resolve({ file, width, height })
    }

    image.onload = () => {
      canvas.width = nextWidth
      canvas.height = nextHeight
      context.drawImage(image, 0, 0, nextWidth, nextHeight)
      revokeObjectUrl(url)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file, width, height })
            return
          }

          const nextFile = new File(
            [blob],
            replaceFileExtension(
              file.name,
              EXTENSION_BY_MIME[mimeType] ?? getExtension(file),
            ),
            {
              type: mimeType,
              lastModified: Date.now(),
            },
          )
          resolve({ file: nextFile, width: nextWidth, height: nextHeight })
        },
        mimeType,
        quality,
      )
    }

    image.onerror = finishWithOriginal
    image.src = url
  })
}

const getValueFileLabel = (
  value: unknown,
  reference?: DynamicImageReference,
) => {
  const upload = getPendingUpload(value)
  if (upload) {
    return `${upload.fileName} - ${formatFileSize(upload.size)}`
  }

  if (reference?.width && reference?.height) {
    return `${reference.width} x ${reference.height}px`
  }

  if (reference?.key) return reference.key
  return undefined
}

const getImageAspectRatio = (metadata?: ImageMetadata) =>
  metadata?.width && metadata.height
    ? metadata.width / metadata.height
    : undefined

const prepareImageUpload = async ({
  file,
  field,
  texts,
  accept,
  maxSizeBytes,
  processing,
  previousReference,
}: {
  file: File
  field: Field
  texts: ResolvedImageTexts
  accept: string
  maxSizeBytes: number
  processing: ImagePreset['processing']
  previousReference?: DynamicImageReference
}): Promise<PreparedUpload> => {
  if (!isImageFile(file)) {
    throw new Error(texts.invalidImageMessage)
  }

  if (!isAcceptedFile(file, accept)) {
    throw new Error(
      texts.unsupportedFormatMessage ??
        `Format non supporté. Formats acceptés : ${accept}.`,
    )
  }

  if (file.size > maxSizeBytes) {
    throw new Error(
      texts.maxSizeMessage ??
        `Image trop lourde. Taille maximale : ${formatFileSize(maxSizeBytes)}.`,
    )
  }

  const metadata = await readImageMetadata(file, texts)

  if (
    field.imageMinWidth &&
    metadata.width &&
    metadata.width < field.imageMinWidth
  ) {
    throw new Error(
      texts.minWidthMessage ??
        `Image trop étroite. Largeur minimale : ${field.imageMinWidth}px.`,
    )
  }

  if (
    field.imageMinHeight &&
    metadata.height &&
    metadata.height < field.imageMinHeight
  ) {
    throw new Error(
      texts.minHeightMessage ??
        `Image trop basse. Hauteur minimale : ${field.imageMinHeight}px.`,
    )
  }

  const shouldProcess = processing.enabled !== false
  const processed = shouldProcess
    ? await resizeImageFile({
        file,
        width: metadata.width,
        height: metadata.height,
        maxWidth: processing.maxWidth,
        maxHeight: processing.maxHeight,
        mimeType: getOutputMimeType(file, processing.output),
        quality: processing.quality,
      })
    : { file, width: metadata.width, height: metadata.height }

  return {
    upload: {
      file: processed.file,
      originalFile: file,
      fileName: processed.file.name,
      contentType: getFileContentType(processed.file),
      size: processed.file.size,
      width: processed.width,
      height: processed.height,
      suggestedKey: getSuggestedKey(field, processed.file),
      previous: previousReference,
    },
  }
}

export const DynamicImageUploadField = ({
  id,
  name,
  field,
  value,
  error,
  disabled,
  ariaDescribedBy,
  ariaInvalid,
  onChange,
  onBlur,
}: DynamicImageUploadFieldProps) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [localError, setLocalError] = React.useState<string | null>(null)
  const [objectUrl, setObjectUrl] = React.useState<string | undefined>()
  const [collectionObjectUrls, setCollectionObjectUrls] = React.useState<
    Record<string, string>
  >({})

  const imageKind = field.imageKind ?? 'cover'
  const preset = IMAGE_PRESETS[imageKind]
  const isMultiple = Boolean(field.imageMultiple ?? field.multiple)
  const collectionValue = isMultiple ? getCollectionValue(value) : null
  const collectionItems = collectionValue?.items ?? []
  const removedReferences = collectionValue?.removed ?? []
  const pendingUpload = getPendingUpload(value)
  const currentReference = getCurrentReference(value)
  const previousReference = getPreviousReference(value)
  const status = getValueStatus(value)
  const accept = field.accept ?? preset.accept
  const label = getSafeLabel(field)
  const singleAspectRatio =
    imageKind === 'profile' ? 1 : (field.imageAspectRatio ?? preset.aspectRatio)
  const singlePreviewAspectRatio =
    imageKind === 'profile'
      ? 1
      : (getImageAspectRatio(pendingUpload) ??
        getImageAspectRatio(currentReference) ??
        singleAspectRatio)
  const singlePreviewMaxHeight = SINGLE_PREVIEW_MAX_HEIGHT_BY_KIND[imageKind]
  const collectionAspectRatio =
    field.imageCardAspectRatio ?? field.imageAspectRatio ?? 4 / 3
  const collectionLayout = field.imageCollectionLayout ?? 'grid'
  const isHorizontalCollection = collectionLayout === 'horizontal-scroll'
  const maxSizeBytes = field.imageMaxSizeBytes ?? preset.maxSizeBytes
  const processing = {
    ...preset.processing,
    ...field.imageProcessing,
  }
  const texts = getImageTexts(preset, field)
  const minImageCount =
    isMultiple && typeof field.imageMinCount === 'number'
      ? field.imageMinCount
      : isMultiple && typeof field.min === 'number'
        ? field.min
        : isMultiple
          ? 0
          : undefined
  const maxImageCount =
    typeof field.imageMaxCount === 'number'
      ? field.imageMaxCount
      : typeof field.max === 'number'
        ? field.max
        : undefined
  const canAddMore =
    !isMultiple ||
    maxImageCount === undefined ||
    collectionItems.length < maxImageCount
  const primarySelectable = Boolean(field.imagePrimarySelectable)
  const isReorderable = Boolean(field.imageReorderable)
  const previewSrc =
    isMultiple || status === 'removed'
      ? undefined
      : (objectUrl ?? currentReference?.url)
  const hasPreview = Boolean(previewSrc)
  const hasSingleValue =
    !isMultiple && status !== 'removed' && Boolean(pendingUpload || currentReference)
  const hasSingleControls = hasPreview || hasSingleValue
  const hasError = Boolean(error || localError)
  const fileLabel = getValueFileLabel(value, currentReference)
  const previewAriaLabel = hasSingleValue
    ? (texts.replaceAriaLabel ?? `${texts.replaceButtonLabel} ${label}`)
    : (texts.addAriaLabel ?? `${texts.chooseButtonLabel} ${label}`)
  const pendingCollectionFilesKey = collectionItems
    .filter((item) => item.status === 'pending')
    .map((item) =>
      item.status === 'pending'
        ? `${item.id}:${item.upload.fileName}:${item.upload.size}:${item.upload.file.lastModified}`
        : item.id,
    )
    .join('|')

  React.useEffect(() => {
    if (isMultiple || !pendingUpload?.file || !canUseObjectUrl()) {
      setObjectUrl(undefined)
      return undefined
    }

    const nextObjectUrl = URL.createObjectURL(pendingUpload.file)
    setObjectUrl(nextObjectUrl)

    return () => {
      revokeObjectUrl(nextObjectUrl)
    }
  }, [isMultiple, pendingUpload?.file])

  React.useEffect(() => {
    if (!isMultiple || !canUseObjectUrl()) {
      setCollectionObjectUrls({})
      return undefined
    }

    const nextObjectUrls: Record<string, string> = {}

    collectionItems.forEach((item) => {
      if (item.status === 'pending') {
        nextObjectUrls[item.id] = URL.createObjectURL(item.upload.file)
      }
    })

    setCollectionObjectUrls(nextObjectUrls)

    return () => {
      Object.values(nextObjectUrls).forEach(revokeObjectUrl)
    }
  }, [isMultiple, pendingCollectionFilesKey])

  const openFileDialog = () => {
    if (disabled || isProcessing) return
    inputRef.current?.click()
  }

  const resetInputValue = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const rejectFile = (message: string) => {
    setLocalError(message)
    resetInputValue()
  }

  const handleSingleFile = async (file: File) => {
    if (disabled) return

    setLocalError(null)
    setIsProcessing(true)

    try {
      const prepared = await prepareImageUpload({
        file,
        field,
        texts,
        accept,
        maxSizeBytes,
        processing,
        previousReference,
      })

      onChange({ status: 'pending', upload: prepared.upload })
      onBlur()
      resetInputValue()
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : texts.prepareErrorMessage
      rejectFile(message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleMultipleFiles = async (files: File[]) => {
    if (disabled || files.length === 0) return

    setLocalError(null)

    const availableSlots =
      maxImageCount === undefined
        ? files.length
        : Math.max(maxImageCount - collectionItems.length, 0)

    if (availableSlots <= 0) {
      rejectFile(
        texts.maxCountMessage ??
          `Nombre maximal d'images atteint (${maxImageCount}).`,
      )
      return
    }

    const filesToPrepare = files.slice(0, availableSlots)
    const skippedCount = files.length - filesToPrepare.length
    const preparedItems: DynamicImageCollectionItem[] = []
    const errors: string[] = []

    setIsProcessing(true)

    try {
      for (const file of filesToPrepare) {
        try {
          const prepared = await prepareImageUpload({
            file,
            field,
            texts,
            accept,
            maxSizeBytes,
            processing,
          })

          preparedItems.push({
            id: getRandomId(),
            status: 'pending',
            upload: prepared.upload,
            order: collectionItems.length + preparedItems.length,
          })
        } catch (caughtError) {
          const message =
            caughtError instanceof Error
              ? caughtError.message
              : texts.prepareErrorMessage
          errors.push(`${file.name}: ${message}`)
        }
      }

      if (preparedItems.length > 0) {
        onChange({
          items: reorderCollectionItems(
            [...collectionItems, ...preparedItems],
            primarySelectable,
          ),
          removed: removedReferences,
        })
        onBlur()
      }

      if (skippedCount > 0 || errors.length > 0) {
        const countMessage =
          skippedCount > 0
            ? (texts.maxCountMessage ??
              `${skippedCount} image(s) ignorée(s), limite atteinte.`)
            : undefined
        setLocalError([countMessage, ...errors].filter(Boolean).join(' '))
      }

      resetInputValue()
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFiles = (files: File[]) => {
    if (files.length === 0) return
    if (isMultiple) {
      void handleMultipleFiles(files)
      return
    }

    void handleSingleFile(files[0])
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(event.target.files ?? []))
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (disabled || isProcessing) return
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (disabled || isProcessing) return

    handleFiles(Array.from(event.dataTransfer.files ?? []))
  }

  const handleRemove = () => {
    setLocalError(null)
    if (previousReference || currentReference) {
      onChange({
        status: 'removed',
        previous: previousReference ?? currentReference,
      })
    } else {
      onChange(null)
    }
    onBlur()
  }

  const removeCollectionItem = (itemToRemove: DynamicImageCollectionItem) => {
    setLocalError(null)

    const nextRemoved =
      itemToRemove.status === 'existing'
        ? [...removedReferences, itemToRemove.ref]
        : removedReferences
    const nextItems = collectionItems.filter(
      (item) => item.id !== itemToRemove.id,
    )

    onChange({
      items: reorderCollectionItems(nextItems, primarySelectable),
      removed: nextRemoved,
    })
    onBlur()
  }

  const setPrimaryCollectionItem = (
    itemToPromote: DynamicImageCollectionItem,
  ) => {
    if (!primarySelectable) return

    onChange({
      items: reorderCollectionItems(
        collectionItems.map((item) => ({
          ...item,
          isPrimary: item.id === itemToPromote.id,
        })),
        primarySelectable,
      ),
      removed: removedReferences,
    })
    onBlur()
  }

  const moveCollectionItem = (
    itemToMove: DynamicImageCollectionItem,
    direction: -1 | 1,
  ) => {
    if (!isReorderable) return

    const orderedItems = reorderCollectionItems(
      collectionItems,
      primarySelectable,
    )
    const currentIndex = orderedItems.findIndex(
      (item) => item.id === itemToMove.id,
    )
    const nextIndex = currentIndex + direction

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= orderedItems.length)
      return

    const nextItems = [...orderedItems]
    const [movedItem] = nextItems.splice(currentIndex, 1)
    nextItems.splice(nextIndex, 0, movedItem)

    onChange({
      items: reorderCollectionItems(nextItems, primarySelectable),
      removed: removedReferences,
    })
    onBlur()
  }

  const renderError = () =>
    localError ? (
      <div
        role='alert'
        className='flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
      >
        <XCircleIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
        <span>{localError}</span>
      </div>
    ) : null

  const renderInput = () => (
    <input
      ref={inputRef}
      id={id}
      name={name}
      type='file'
      className='sr-only'
      accept={accept}
      multiple={isMultiple}
      disabled={disabled || isProcessing}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      onChange={handleInputChange}
      onBlur={onBlur}
    />
  )

  const baseDropZoneClassName = cx(
    'rounded-md border bg-white transition-colors',
    isDragging ? 'border-input-400 bg-neutral-50' : 'border-border',
    hasError && 'border-destructive/70',
    disabled && 'opacity-60',
  )

  const renderEmptyContent = (title: string, hint: string) => (
    <div className='flex max-w-64 flex-col items-center gap-2 px-4 text-center'>
      <FilePlusIcon className='size-9 text-slate-100' aria-hidden='true' />
      <span className='text-sm font-medium text-slate-700'>{title}</span>
      <span className='text-xs leading-5 text-slate-50'>{hint}</span>
    </div>
  )

  const iconButtonClassName =
    'size-7 shrink-0 p-0 text-slate-500 hover:text-slate-700'

  const singlePreviewStyle: React.CSSProperties = hasPreview
    ? {
        aspectRatio: singlePreviewAspectRatio,
        maxHeight: singlePreviewMaxHeight,
        width: `min(100%, ${Math.round(
          singlePreviewMaxHeight * singlePreviewAspectRatio,
        )}px)`,
      }
    : {
        aspectRatio: singleAspectRatio,
      }

  const renderSingleUpload = () => (
    <div
      className={cx(
        baseDropZoneClassName,
        hasPreview ? 'overflow-hidden' : 'space-y-3 p-3',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-image-upload-kind={imageKind}
    >
      <button
        type='button'
        className={cx(
          'group relative flex w-full items-center justify-center overflow-hidden bg-white text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          hasPreview
            ? 'border-0'
            : 'min-h-40 border border-dashed border-input-300',
          hasPreview && 'mx-auto',
          imageKind === 'profile' ? 'rounded-full' : 'rounded-md',
          preset.previewClassName,
          !disabled && 'hover:border-input-700',
        )}
        style={singlePreviewStyle}
        onClick={openFileDialog}
        disabled={disabled || isProcessing}
        aria-label={previewAriaLabel}
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={field.imagePreviewAlt ?? currentReference?.alt ?? label}
            className={cx(
              'h-full w-full bg-white',
              preset.objectFit === 'cover'
                ? 'object-cover'
                : 'object-contain p-4',
            )}
          />
        ) : (
          renderEmptyContent(
            texts.emptyTitle,
            field.placeholder ?? texts.emptyHint,
          )
        )}

        {isProcessing && (
          <div className='absolute inset-0 flex items-center justify-center bg-white/75'>
            <CircleNotchIcon className='size-7 animate-spin text-slate-500' />
          </div>
        )}
      </button>

      {hasSingleControls && (
        <div className='flex h-10 min-h-10 max-h-10 items-center gap-1 border-t border-border px-2'>
          <p className='min-w-0 flex-1 truncate px-1 text-xs leading-5 text-slate-100'>
            {fileLabel ?? field.placeholder ?? texts.emptyHint}
          </p>
          <Button
            type='button'
            variant='ghost'
            size='none'
            onClick={openFileDialog}
            disabled={disabled || isProcessing}
            title={texts.replaceButtonLabel}
            aria-label={
              texts.replaceAriaLabel ?? `${texts.replaceButtonLabel} ${label}`
            }
            className={iconButtonClassName}
          >
            <ArrowsClockwiseIcon className='size-4' aria-hidden='true' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='none'
            onClick={handleRemove}
            disabled={disabled || isProcessing}
            title={texts.removeButtonLabel}
            aria-label={
              texts.removeAriaLabel ?? `${texts.removeButtonLabel} ${label}`
            }
            className={cx(iconButtonClassName, 'hover:text-destructive')}
          >
            <XIcon className='size-4' aria-hidden='true' />
          </Button>
        </div>
      )}

      {renderError()}
      {renderInput()}
    </div>
  )

  const renderCollectionItem = (
    item: DynamicImageCollectionItem,
    itemIndex: number,
  ) => {
    const itemReference = getCollectionItemReference(item)
    const itemPreviewSrc =
      item.status === 'pending'
        ? collectionObjectUrls[item.id]
        : itemReference?.url
    const itemLabel = getCollectionItemFileLabel(item)

    return (
      <div
        key={item.id}
        className={cx(
          'overflow-hidden rounded-md border border-border bg-white',
          isHorizontalCollection && 'w-64 shrink-0',
        )}
      >
        <button
          type='button'
          className='relative flex w-full items-center justify-center overflow-hidden bg-slate-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
          style={{ aspectRatio: collectionAspectRatio }}
          onClick={() => setPrimaryCollectionItem(item)}
          disabled={disabled || isProcessing || !primarySelectable}
          aria-label={
            primarySelectable
              ? `${texts.primaryLabel} ${itemIndex + 1}`
              : `${label} ${itemIndex + 1}`
          }
        >
          {itemPreviewSrc ? (
            <img
              src={itemPreviewSrc}
              alt={itemReference?.alt ?? `${label} ${itemIndex + 1}`}
              className={cx(
                'h-full w-full bg-white',
                preset.objectFit === 'cover'
                  ? 'object-cover'
                  : 'object-contain p-3',
              )}
            />
          ) : (
            renderEmptyContent(label, texts.emptyHint)
          )}
        </button>

        <div className='flex h-8 min-h-8 max-h-8 items-center gap-1 border-t border-border px-2'>
          <div className='flex shrink-0 items-center gap-0.5'>
            {isReorderable && (
              <>
                <Button
                  type='button'
                  variant='ghost'
                  size='none'
                  onClick={() => moveCollectionItem(item, -1)}
                  disabled={disabled || isProcessing || itemIndex === 0}
                  title='Déplacer vers la gauche'
                  aria-label={`Déplacer ${label} ${itemIndex + 1} vers la gauche`}
                  className={iconButtonClassName}
                >
                  <CaretLeftIcon className='size-4' aria-hidden='true' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='none'
                  onClick={() => moveCollectionItem(item, 1)}
                  disabled={
                    disabled ||
                    isProcessing ||
                    itemIndex === collectionItems.length - 1
                  }
                  title='Déplacer vers la droite'
                  aria-label={`Déplacer ${label} ${itemIndex + 1} vers la droite`}
                  className={iconButtonClassName}
                >
                  <CaretRightIcon className='size-4' aria-hidden='true' />
                </Button>
              </>
            )}
            {primarySelectable && (
              <Button
                type='button'
                variant='ghost'
                size='none'
                onClick={() => setPrimaryCollectionItem(item)}
                disabled={disabled || isProcessing || item.isPrimary}
                title={
                  item.isPrimary
                    ? 'Image principale'
                    : 'Définir comme image principale'
                }
                aria-label={
                  item.isPrimary
                    ? `${label} ${itemIndex + 1} est l'image principale`
                    : `Définir ${label} ${itemIndex + 1} comme image principale`
                }
                className={cx(
                  iconButtonClassName,
                  item.isPrimary && 'text-primary-500 opacity-100',
                )}
              >
                <Star
                  className='size-4'
                  weight={item.isPrimary ? 'fill' : 'regular'}
                  aria-hidden='true'
                />
              </Button>
            )}
          </div>

          {itemLabel && (
            <p className='min-w-0 flex-1 truncate px-1 text-center text-xs leading-5 text-slate-100'>
              {itemLabel}
            </p>
          )}

          <Button
            type='button'
            variant='ghost'
            size='none'
            onClick={() => removeCollectionItem(item)}
            disabled={disabled || isProcessing}
            title={texts.removeButtonLabel}
            aria-label={`${texts.removeButtonLabel} ${label} ${itemIndex + 1}`}
            className={cx(
              iconButtonClassName,
              'ml-auto hover:text-destructive',
            )}
          >
            <XIcon className='size-4' aria-hidden='true' />
          </Button>
        </div>
      </div>
    )
  }

  const renderMultiUpload = () => {
    const countLabel =
      maxImageCount === undefined
        ? `${collectionItems.length} image(s)`
        : `${collectionItems.length} / ${maxImageCount} image(s)`
    const minCountLabel =
      minImageCount && minImageCount > 0
        ? `minimum ${minImageCount}`
        : undefined
    const addButton = canAddMore ? (
      <button
        type='button'
        className={cx(
          'flex h-full min-h-36 items-center justify-center rounded-md border border-dashed border-input-300 bg-white transition-colors hover:border-input-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
          isHorizontalCollection ? 'w-64 shrink-0' : 'w-full',
        )}
        style={
          isHorizontalCollection
            ? {
                height: `calc((16rem - 2px) / ${collectionAspectRatio} + 2rem + 2px)`,
              }
            : undefined
        }
        onClick={openFileDialog}
        disabled={disabled || isProcessing}
        aria-label={
          texts.addAriaLabel ?? `${texts.addMoreButtonLabel} ${label}`
        }
      >
        {renderEmptyContent(
          collectionItems.length > 0
            ? texts.addMoreButtonLabel
            : texts.emptyTitle,
          field.placeholder ?? texts.emptyHint,
        )}
      </button>
    ) : null
    const collectionContent = (
      <>
        {collectionItems.map(renderCollectionItem)}
        {addButton}
      </>
    )
    const horizontalAddAction =
      isHorizontalCollection && canAddMore ? (
        <Button
          type='button'
          variant='action'
          size='sm'
          onClick={openFileDialog}
          disabled={disabled || isProcessing}
          aria-label={texts.addAriaLabel ?? `${texts.addButtonLabel} ${label}`}
          className='shrink-0'
        >
          <PlusCircleIcon className='size-4' aria-hidden='true' />
          {texts.addButtonLabel}
        </Button>
      ) : null

    return (
      <div
        className={cx(baseDropZoneClassName, 'space-y-3 p-3')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-image-upload-kind={imageKind}
        data-image-upload-multiple='true'
      >
        {isHorizontalCollection ? (
          <ScrollArea
            type='always'
            className='w-full pb-2'
            noVerticalScroll
            scrollBarCn='mt-2'
          >
            <div className='flex items-stretch gap-3 pb-2 pr-1'>
              {collectionContent}
            </div>
          </ScrollArea>
        ) : (
          <div className='grid items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3'>
            {collectionContent}
          </div>
        )}

        <div className='flex flex-wrap items-center justify-between gap-2 text-xs leading-5 text-slate-50'>
          <span>{[countLabel, minCountLabel].filter(Boolean).join(' - ')}</span>
          <div className='ml-auto flex items-center gap-2'>
            {isProcessing && (
              <span className='inline-flex items-center gap-1 text-slate-500'>
                <CircleNotchIcon
                  className='size-3.5 animate-spin'
                  aria-hidden='true'
                />
                Préparation des images
              </span>
            )}
            {horizontalAddAction}
          </div>
        </div>

        {renderError()}
        {renderInput()}
      </div>
    )
  }

  return isMultiple ? renderMultiUpload() : renderSingleUpload()
}
